<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';
import AgentVoiceProfileForm from './components/AgentVoiceProfileForm.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  displayName: {
    type: String,
    default: '',
  },
  messageSignature: {
    type: String,
    default: '',
  },
  customAttributes: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  autoOffline: {
    type: Boolean,
    default: true,
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentDisplayName = ref(props.displayName);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });
const agentEmail = ref(props.email);
const agentMessageSignature = ref(props.messageSignature);
const agentWhatsapp = ref(
  props.customAttributes.manager_phone_e164 ||
    props.customAttributes.phone_e164 ||
    props.customAttributes.whatsapp ||
    props.customAttributes.telefone ||
    props.customAttributes.phone ||
    ''
);
const autoOffline = ref(props.autoOffline);

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
    {
      id: 'captador',
      name: 'captador',
      label: t('AGENT_MGMT.AGENT_TYPES.CAPTADOR'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      display_name: agentDisplayName.value,
      email: agentEmail.value,
      availability: agentAvailability.value,
      auto_offline: autoOffline.value,
      message_signature: agentMessageSignature.value,
      custom_attributes: {
        manager_phone_e164: agentWhatsapp.value.trim(),
        phone_e164: agentWhatsapp.value.trim(),
        whatsapp: agentWhatsapp.value.trim(),
      },
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col overflow-auto bg-white">
    <header class="border-b border-[#eee5d6] px-6 py-5">
      <h2 class="font-redHatDisplay text-[18px] font-semibold text-[#1a1a1a]">
        {{ pageTitle }}
      </h2>
      <p class="mt-2 text-[14px] leading-6 text-[#667085]">
        {{ $t('AGENT_MGMT.EDIT.DESC') }}
      </p>
    </header>
    <form class="flex flex-col gap-5 px-6 py-6" @submit.prevent="editAgent">
      <div class="w-full">
        <label
          class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]"
          :class="{ '!text-[#e5646f]': v$.agentName.$error }"
        >
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
        </label>
        <input
          v-model="agentName"
          type="text"
          class="h-11 w-full rounded-lais border px-3 text-[14px] text-[#1a1a1a] outline-none transition placeholder:text-[#98a2b3] focus:border-[#49317a]"
          :class="v$.agentName.$error ? 'border-[#e5646f]' : 'border-[#d9d2e6]'"
          :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
          @input="v$.agentName.$touch"
        />
      </div>

      <div class="w-full">
        <label class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]">
          {{ $t('AGENT_MGMT.EDIT.FORM.DISPLAY_NAME.LABEL') }}
        </label>
        <input
          v-model="agentDisplayName"
          type="text"
          class="h-11 w-full rounded-lais border border-[#d9d2e6] px-3 text-[14px] text-[#1a1a1a] outline-none transition placeholder:text-[#98a2b3] focus:border-[#49317a]"
          :placeholder="$t('AGENT_MGMT.EDIT.FORM.DISPLAY_NAME.PLACEHOLDER')"
        />
      </div>

      <div class="w-full">
        <label class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]">
          {{ $t('AGENT_MGMT.EDIT.FORM.EMAIL.LABEL') }}
        </label>
        <input
          v-model="agentEmail"
          type="email"
          class="h-11 w-full rounded-lais border border-[#d9d2e6] px-3 text-[14px] text-[#1a1a1a] outline-none transition placeholder:text-[#98a2b3] focus:border-[#49317a]"
          :placeholder="$t('AGENT_MGMT.EDIT.FORM.EMAIL.PLACEHOLDER')"
        />
      </div>

      <div class="w-full">
        <label
          class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]"
          :class="{ '!text-[#e5646f]': v$.selectedRoleId.$error }"
        >
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
        </label>
        <select
          v-model="selectedRoleId"
          class="h-11 w-full rounded-lais border px-3 text-[14px] text-[#1a1a1a] outline-none transition focus:border-[#49317a]"
          :class="
            v$.selectedRoleId.$error ? 'border-[#e5646f]' : 'border-[#d9d2e6]'
          "
          @change="v$.selectedRoleId.$touch"
        >
          <option v-for="role in roles" :key="role.id" :value="role.id">
            {{ role.label }}
          </option>
        </select>
        <span
          v-if="v$.selectedRoleId.$error"
          class="mt-2 block text-[12px] text-[#e5646f]"
        >
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
        </span>
      </div>

      <div class="w-full">
        <label class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]">
          {{ $t('AGENT_MGMT.EDIT.FORM.WHATSAPP.LABEL') }}
        </label>
        <input
          v-model="agentWhatsapp"
          type="tel"
          class="h-11 w-full rounded-lais border border-[#d9d2e6] px-3 text-[14px] text-[#1a1a1a] outline-none transition placeholder:text-[#98a2b3] focus:border-[#49317a]"
          :placeholder="$t('AGENT_MGMT.EDIT.FORM.WHATSAPP.PLACEHOLDER')"
        />
      </div>

      <div class="w-full">
        <label
          class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]"
          :class="{ '!text-[#e5646f]': v$.agentAvailability.$error }"
        >
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
        </label>
        <select
          v-model="agentAvailability"
          class="h-11 w-full rounded-lais border px-3 text-[14px] text-[#1a1a1a] outline-none transition focus:border-[#49317a]"
          :class="
            v$.agentAvailability.$error
              ? 'border-[#e5646f]'
              : 'border-[#d9d2e6]'
          "
          @change="v$.agentAvailability.$touch"
        >
          <option
            v-for="status in availabilityStatuses"
            :key="status.value"
            :value="status.value"
          >
            {{ status.label }}
          </option>
        </select>
        <span
          v-if="v$.agentAvailability.$error"
          class="mt-2 block text-[12px] text-[#e5646f]"
        >
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
        </span>
      </div>

      <label
        class="flex min-h-11 items-center gap-3 rounded-lais border border-[#d9d2e6] px-3 text-[13px] font-semibold text-[#1a1a1a]"
      >
        <input
          v-model="autoOffline"
          type="checkbox"
          class="size-4 accent-[#49317a]"
        />
        {{ $t('AGENT_MGMT.EDIT.FORM.AUTO_OFFLINE.LABEL') }}
      </label>

      <div class="w-full">
        <label class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]">
          {{ $t('AGENT_MGMT.EDIT.FORM.MESSAGE_SIGNATURE.LABEL') }}
        </label>
        <textarea
          v-model="agentMessageSignature"
          rows="3"
          class="w-full rounded-lais border border-[#d9d2e6] px-3 py-2 text-[14px] text-[#1a1a1a] outline-none transition placeholder:text-[#98a2b3] focus:border-[#49317a]"
          :placeholder="
            $t('AGENT_MGMT.EDIT.FORM.MESSAGE_SIGNATURE.PLACEHOLDER')
          "
        />
      </div>

      <AgentVoiceProfileForm :agent-id="id" />

      <div
        class="flex flex-col gap-3 border-t border-[#f1ebf8] pt-5 lg:flex-row lg:items-center lg:justify-between"
      >
        <button
          v-if="provider !== 'saml'"
          type="button"
          class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-[#d9d2e6] px-4 text-[13px] font-semibold text-[#49317a] transition hover:bg-[#f7f4fb]"
          @click.prevent="resetPassword"
        >
          <span class="i-lucide-lock-keyhole size-4" aria-hidden="true" />
          {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON') }}
        </button>
        <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center rounded-lais border border-[#d9d2e6] px-4 text-[13px] font-semibold text-[#667085] transition hover:bg-[#f7f4fb]"
            @click.prevent="emit('close')"
          >
            {{ $t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT') }}
          </button>
          <button
            type="submit"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lais bg-[#49317a] px-4 text-[13px] font-semibold text-white transition hover:bg-[#3d2966] disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="v$.$invalid || uiFlags.isUpdating"
          >
            <span
              v-if="uiFlags.isUpdating"
              class="i-lucide-loader-circle size-4 animate-spin"
              aria-hidden="true"
            />
            {{ $t('AGENT_MGMT.EDIT.FORM.SUBMIT') }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>
